import React, { useEffect, useMemo, useState } from 'react';
import MaterialReactTable from 'material-react-table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LabelList } from 'recharts';
import { DatabaseManager } from 'helpers/db';
import { constants } from 'buffer';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const dbManager = new DatabaseManager();

const safeFixed = (val, digits = 2) => {
  const num = parseFloat(val);
  return isNaN(num) ? '-' : num.toFixed(digits);
};

const AggregatedGraph = ({ agg }) => {
  const free = parseFloat(agg.freeSpace || 0);
  const used = parseFloat(agg.totalUsedSpace || 0);
  const total = parseFloat(agg.capacity || 0);

  const chartData = [
    {
    //   name: 'Storage',
      Free: total == 0 ? ((free/1)* 100) : ((free/total)* 100),
      Used: total == 0 ? ((free/1)* 100) : ((used/total) * 100),
    },
  ];


  return (
    <ResponsiveContainer width={150} height={30} style={{ overflow: 'visible' }}>
      <BarChart data={chartData} layout="vertical" stackOffset="expand">
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" hide />
         <Tooltip
          formatter={(value) => `${value.toFixed(2)}%`}
          wrapperStyle={{ zIndex: 1000, overflow: 'visible' }}
        />
        <Bar dataKey="Used" stackId="a" fill="#ff6b6b"> 
        </Bar>
        <Bar dataKey="Free" stackId="a" fill="#51cf66">
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const tealTheme = createTheme({
  palette: {
    primary: {
      main: 'rgb(0,133,124)',
    },
  },
});

const CommvaultMui = () => {
  const [data, setData] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await dbManager.getAllObjects(
          process.env.REACT_APP_DYNAMO_DB_COMMVAULT_INITIAL_DATA || ''
        );
        const filtered = (res.data || []).filter(item => item.current_indicator === 1);
        setData(filtered);
        console.log(res.data)
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const groupedData = useMemo(() => {
    const map = {};
    data.forEach(row => {
      const site = row.PK;
      if (!map[site]) {
        map[site] = {
          PK: site,
          commserve: row.commserve,
          siteType: row.siteType,
          freeSpace: 0,
          totalUsedSpace: 0,
          capacity: 0,
          subRows: [],
        };
      }
      map[site].freeSpace += parseFloat(row.freeSpace || 0);
      map[site].totalUsedSpace += parseFloat(row.totalUsedSpace || 0);
      map[site].capacity += parseFloat(row.capacity || 0);
      map[site].subRows.push({...row, siteType: row?.siteType});
    });
    return Object.values(map).map(group => {
      const util = group.capacity
        ? (group.totalUsedSpace / group.capacity) * 100
        : 0;
      return { ...group, utilization: util };
    });
  }, [data]);

  const parentCols = useMemo(() => [
    { accessorKey: 'PK', header: 'Site Code',
      Cell: ({ row }) => (
      <span
        style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
        onClick={() => navigate('/master-commvault', { state: { site_code: row.original.PK , commserve: row.original.commserve} })}
      >
        {row.original.PK}
      </span>
  ),
    },
    {
      accessorKey: 'siteType',
      header: 'Site Type',
      filterVariant: 'select', 
      // filterSelectOptions: Array.from(new Set(data.map(d => d?.siteType).filter(Boolean))),
      filterSelectOptions: ["ROBO_SITE","DATA_CENTER"],
      // enableColumnFilter: true,
    },
    // { accessorKey: 'commserve', header: 'Commserve' },
    {
      accessorKey: 'freeSpace',
      header: 'Aggregated Free (TB)',
      Cell: ({ cell }) => safeFixed(cell.getValue()),
    },
    {
      accessorKey: 'totalUsedSpace',
      header: 'Aggregated Used (TB)',
      Cell: ({ cell }) => safeFixed(cell.getValue()),
    },
    {
      accessorKey: 'capacity',
      header: 'Aggregated Capacity (TB)',
      Cell: ({ cell }) => safeFixed(cell.getValue()),
    },
    {
      accessorKey: 'utilization',
      header: 'Aggregated Utilization (%)',
      Cell: ({ cell }) => `${safeFixed(cell.getValue())}%`,
    },
    {
      id: 'graph',
      header: 'Graph',
      Cell: ({ row }) => <AggregatedGraph agg={row.original} />,
    },
  ], []);

  const childRowHeader = [
    'Storage Pool',
    'Commserve',
    'Free Space (TB)',
    'Used Space (TB)',
    'Capacity (TB)',
    // 'Utilization (%)',
    'Library Type',
    'Model',
    'Generation',
    'Number of Nodes'
  ];

  return (
    <div style={{
      padding: 40,
      backgroundColor: 'white',
      border: '3px solid #f6f6f6',
      borderRadius: 10,
    }}>
    {groupedData.length > 0 && (
      <ThemeProvider theme={tealTheme}>
        <MaterialReactTable
          columns={parentCols}
          data={groupedData}
          enableExpanding
          enableColumnFilters
          getSubRows={row => row}
          renderDetailPanel={({ row }) => (
            <div style={{ margin: '12px',}}>
              {/* Header Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(9, 1fr)',
                fontWeight: 'bold',
                backgroundColor: 'rgb(0,133,124)',
                color: 'white',
                padding: '8px 4px',
              }}>
                {childRowHeader.map((head, idx) => (
                  <div style={{  justifySelf: 'center' }}key={idx}>{head}</div>
                ))}
              </div>
              {/* Data Rows */}
              {(row.original.subRows || []).map((child, idx) => {
                const used = parseFloat(child.totalUsedSpace || 0);
                const cap = parseFloat(child.capacity || 1);
                const util = cap ? (used / cap) * 100 : 0;

              //   const bgColor =
              //     util > 75 ? '#ffdddd' :
              //     util >= 50 ? '#fff4cc' :
              //     '#ddffdd';

                return (
                  <div
                    key={idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(9, 1fr)',
                      borderBottom: '1px solid #ccc',
                      border: '1px solid #ccc',
                      padding: '8px 4px',
                      fontSize: '14px',
                    }}
                  >
                    {/* <div style={{  justifySelf: 'center', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '500px', width: '100%',  whiteSpace: 'nowrap', }}  title={child.SK} >{child.SK}</div> */}
                      <div
                      style={{
                          justifySelf: 'center',
                          whiteSpace: 'normal', 
                          wordBreak: 'break-word', 
                          textAlign: 'center',
                          padding: '4px',
                          minWidth: '150px',
                      }}
                      >
                      {child.SK.split('|')[1]}
                      </div>                  
                    <div style={{  justifySelf: 'center' }}>{child.commserve}</div>
                    <div style={{  justifySelf: 'center' }}>{safeFixed(child.freeSpace)}</div>
                    <div style={{  justifySelf: 'center' }}>{safeFixed(child.totalUsedSpace)}</div>
                    <div style={{  justifySelf: 'center' }}>{safeFixed(child.capacity)}</div>
                    {/* <div style={{  justifySelf: 'center' }}>{safeFixed(util)}%</div> */}
                    <div style={{  justifySelf: 'center' }}>{child.libraryType}</div>
                    <div style={{  justifySelf: 'center' }}>{child.model}</div>
                    <div style={{  justifySelf: 'center' }}>{child.generation}</div>
                    <div style={{  justifySelf: 'center' }}>{child.numberOfNodes}</div>
                  </div>
                );
              })}
            </div>
          )}


          muiTableHeadCellProps={{
            sx: {
              backgroundColor: 'rgb(0,133,124)',
              color: 'white',
              fontWeight: 'bold',
              // borderRight: '1px dashed white',
            },
          }}
          muiTablePaperProps={{
            sx: { boxShadow: 'none', border: '1px solid #ccc' },
          }}
           muiTableBodyCellProps={{
              sx: {
              overflow: 'visible',
              },
          }}
          muiColumnActionsButtonProps={{
            sx: {
              color: 'rgb(0,133,124)',
            },
          }}
          muiShowHideColumnsButtonProps={{
            sx: {
              color: 'rgb(0,133,124)',
            },
          }}
          muiToggleButtonProps={{
            sx: {
              '&.Mui-selected': {
                backgroundColor: 'rgb(0,133,124)',
                color: 'white',
              },
              '&.Mui-selected:hover': {
                backgroundColor: 'rgba(0,133,124,0.8)',
              },
            },
          }}
          muiShowHideColumnsMenuProps={{
            sx: {
              // Switch checked thumb and track
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: 'rgb(0,133,124) !important', // thumb
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: 'rgb(0,133,124) !important', // track
                opacity: 1,
              },
              // Switch default track
              '& .MuiSwitch-track': {
                backgroundColor: '#b2dfdb !important',
                opacity: 1,
              },
              // Switch thumb (checked and unchecked)
              '& .MuiSwitch-thumb': {
                color: 'rgb(0,133,124) !important',
              },
              // Checkbox checked and unchecked
              '& .MuiCheckbox-root': {
                color: 'rgb(0,133,124) !important',
              },
              '& .MuiCheckbox-root.Mui-checked': {
                color: 'rgb(0,133,124) !important',
              },
              // Label and text
              '& .MuiFormControlLabel-label': {
                color: 'rgb(0,133,124) !important',
              },
              '& .MuiTypography-root': {
                color: 'rgb(0,133,124) !important',
              },
              // Hide/Show All button
              '& .MuiButton-root': {
                color: 'rgb(0,133,124) !important',
              },
            },
          }}
          muiTableToolbarProps={{
            sx: {
              '& .MuiIconButton-root': {
                color: 'rgb(0,133,124)',
              },
              '& .MuiIconButton-root:hover': {
                backgroundColor: 'rgba(0,133,124,0.1)',
              },
            },
          }}
        />
      </ThemeProvider>
    )}
    </div>
  );
};

export default CommvaultMui;
